import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import { signOut } from "../../utils/auth";
import { supabase } from "../../lib/supabaseClient";

export default function Navbar({ scrollY = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isAdminHost = hostname === 'admin.gamemeetsworld.com';
  const isLocalHost = ['localhost', '127.0.0.1'].includes(hostname) || hostname.endsWith('.local');
  const showAdminActions = isAdminHost || isLocalHost;
  const [hoveredItem, setHoveredItem] = useState(null);
  const [authed, setAuthed] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  useEffect(() => {
    const refreshAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setAuthed(Boolean(data.session));
    };
    refreshAuth();
    window.addEventListener('authchange', refreshAuth);
    return () => {
      window.removeEventListener('authchange', refreshAuth);
    };
  }, []);

  // Memoize nav items for performance
  const navItems = useMemo(() => [
    { label: "Home", path: "/" },
    { label: "Creators", path: "/creators" },
    { label: "Store", path: "/store" },
    { label: "Gallery", path: "/gallery" },
    { label: "Apply", path: "/apply" }
  ], []);

  const handleNavClick = (item) => {
    setMobileActionsOpen(false);
    navigate(item.path);
  };

  // Determine active nav item based on current route
  const getActiveIndex = () => {
    if (location.pathname === "/") return 0;
    if (location.pathname === "/creators") return 1;
    if (location.pathname === "/store") return 2;
    if (location.pathname === "/gallery") return 3;
    if (location.pathname === "/apply") return 4;
    return -1;
  };

  const handleLogout = async () => {
    await signOut();
    setAuthed(false);
    setMobileActionsOpen(false);
    navigate('/');
  };

  const currentActive = getActiveIndex();

  useEffect(() => {
    setMobileActionsOpen(false);
  }, [location.pathname]);

  // Navbar should be solid on non-home pages, transparent only on home page at top of scroll
  const isHomePage = location.pathname === "/";
  const navClasses = !isHomePage
    ? "bg-game-black/95 backdrop-blur-lg border-b border-accent-green/20 py-3"
    : scrollY > 50
      ? "bg-game-black/95 backdrop-blur-lg border-b border-accent-green/20 py-3"
      : "bg-game-black/0 backdrop-blur-none py-6";

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={`
        fixed top-0 w-full z-50
        transition-[background,backdrop-filter,padding]
        duration-150 ease-linear
        ${navClasses}
      `}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-12 flex justify-between items-center gap-2">

        {/* LOGO */}
        <motion.button
          className="flex items-center cursor-pointer group relative focus:outline-none focus:ring-2 focus:ring-accent-green focus:ring-offset-2 focus:ring-offset-game-black rounded-lg"
          onClick={() => navigate("/")}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          aria-label="Go to homepage"
        >
          <img
            src={logo}
            alt="GameMeetsWorld Logo"
            className="h-7 sm:h-9 lg:h-10 w-auto object-contain relative z-10"
          />
          <span className="ml-2 lg:ml-3 text-white font-bold text-base lg:text-lg hidden md:block tracking-wide group-hover:text-accent-green transition-colors duration-150">
            GameMeetsWorld
          </span>
        </motion.button>

        {/* MOBILE NAV (same row as logo) */}
        <div className="flex-1 lg:hidden min-w-0">
          <div className="flex items-center justify-end gap-1 overflow-x-auto whitespace-nowrap no-scrollbar">
            {navItems.map((item, idx) => {
              const isActive = currentActive === idx;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item)}
                  className={`flex-shrink-0 px-2 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${
                    isActive
                      ? "bg-accent-green text-black"
                      : "bg-white/5 text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={`Navigate to ${item.label}`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* DESKTOP MENU */}
        <div className="hidden lg:flex items-center gap-1" role="menubar">
          {navItems.map((item, idx) => {
            const isActive = currentActive === idx;
            return (
              <motion.button
                key={item.path}
                onClick={() => handleNavClick(item, idx)}
                onHoverStart={() => setHoveredItem(idx)}
                onHoverEnd={() => setHoveredItem(null)}
                className="relative px-5 py-2 rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-accent-green focus:ring-offset-2 focus:ring-offset-transparent"
                whileTap={{ scale: 0.95 }}
                role="menuitem"
                aria-current={isActive ? "page" : undefined}
                aria-label={`Navigate to ${item.label}`}
              >
                {/* Active background */}
                {isActive && (
                  <div
                    className="absolute inset-0 bg-accent-green rounded-full shadow-[0_0_12px_rgba(37,179,85,0.35)]"
                  />
                )}

                {/* Hover effect */}
                {!isActive && hoveredItem === idx && (
                  <motion.div
                    className="absolute inset-0 bg-white/10 rounded-full"
                    layoutId="hoverNav"
                    transition={{ duration: 0.15 }}
                  />
                )}

                <span
                  className={`relative z-10 text-sm font-bold tracking-wide uppercase
                    ${isActive ? "text-black" : "text-white/70"}
                  `}
                >
                  {item.label}
                </span>
              </motion.button>
            );
          })}
        </div>

        {showAdminActions ? (
          <>
            {/* Auth Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              {authed ? (
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="px-4 py-2 text-accent-green font-semibold hover:text-white transition-colors"
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-accent-green/20 text-accent-green border border-accent-green/50 rounded-lg hover:bg-accent-green/30 transition-colors font-semibold"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-accent-green text-black font-semibold rounded-lg hover:bg-[#8FE639] transition-colors"
                >
                  Admin Login
                </button>
              )}
            </div>
            <button
              className="lg:hidden px-2.5 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-accent-green"
              onClick={() => setMobileActionsOpen(!mobileActionsOpen)}
              aria-label="Toggle account actions"
              aria-expanded={mobileActionsOpen}
              aria-controls="mobile-actions"
            >
              {authed ? "Account" : "Admin"}
            </button>
          </>
        ) : null}
      </div>

      {/* MOBILE ACCOUNT ACTIONS */}
      {mobileActionsOpen && showAdminActions && (
        <div
          id="mobile-actions"
          className="lg:hidden border-t border-accent-green/20 bg-game-black/95 backdrop-blur-xl px-3 pb-3"
        >
          {authed ? (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setMobileActionsOpen(false);
                }}
                className="px-3 py-2 rounded-lg bg-accent-green/15 text-accent-green text-xs font-bold uppercase"
              >
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="px-3 py-2 rounded-lg bg-red-500/15 text-red-300 text-xs font-bold uppercase"
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="pt-2">
              <button
                onClick={() => {
                  navigate('/login');
                  setMobileActionsOpen(false);
                }}
                className="w-full px-3 py-2 rounded-lg bg-accent-green text-black text-xs font-bold uppercase"
              >
                Admin Login
              </button>
            </div>
          )}
        </div>
      )}
    </motion.nav>
  );
}
